"use client";

import { Euro, HandCoins, Home, MapPin, Users } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { useTranslation } from "@/hooks/useTranslation";
import type { Apartment, Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";

export default function ApartmentCard({ apartment }: { apartment: Apartment }) {
  const { t } = useTranslation();
  return (
    <div className="group overflow-hidden border bg-white shadow transition-all duration-300 hover:-translate-y-8 hover:shadow-xl">
      <Link href={`/apartments/${apartment.slug}`}>
        <div className="relative h-80 w-full md:h-64 xl:h-80">
          <Image
            fill
            src={getImageUrl(apartment.featuredImage)}
            alt={
              (apartment.featuredImage as Media)?.alt || "pension honenau room"
            }
          />
        </div>
      </Link>
      <div className="space-y-4 p-6">
        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="size-4" />
          <span className="text-primary-600">Pension Hohenau</span>
        </div>

        {/* Title */}

        <h3 className="text-xl font-bold text-primary-900 transition-colors duration-200">
          {apartment.name}
        </h3>

        <div className="grid grid-cols-3 gap-4 border-t border-gray-100 py-4">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-primary-50 p-4">
              <Home className="size-8 text-primary-600" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">
                {apartment.attributes?.area}
              </p>
              <p className="text-gray-500">{t({ en: "Area", de: "Fläche" })}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-primary-50 p-4">
              <Users className="size-8 text-primary-600" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">
                {apartment.attributes?.numberOfPeoples}
              </p>
              <p className="text-gray-500">{t({ en: "Guest", de: "Gast" })}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="rounded-full bg-primary-50 p-4">
              <Euro className="size-8 text-primary-600" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">{apartment.price}</p>
              <p className="text-gray-500">
                {t({ en: "Per night", de: "Pro Nacht" })}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-5">
          <Link
            href={"/booking"}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-4 font-semibold text-white transition-all duration-200 hover:bg-primary-600"
          >
            <span>{t({ en: "Book Now", de: "Jetzt Anfragen" })}</span>
            <HandCoins width={20} height={20} />
          </Link>
          <Link
            href={`/apartments/${apartment.slug}`}
            className="font-semibold text-primary-500 underline transition-colors duration-200 hover:text-primary-600"
          >
            <span>{t({ de: "Erkunden", en: "Explore" })}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
